import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ScheduleNow from "../../../_components/steps/appointment/ScheduleNow";
import { RingLoader } from "@/_components";
import { utilities } from "@/_helpers";

const STEPS = {
  SCHEDULE_NOW: "SCHEDULE_NOW",
  DATE_TIME: "DATE_TIME",
};

const PricingTextPilotLocationStep = ({
  isLoaded,
  onLocationSelected,
  submitting,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [modalOpen, setModalOpen] = useState(false);
  const [stepSchedule, setStepSchedule] = useState(STEPS.SCHEDULE_NOW);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [getMap, setGetMap] = useState(false);
  const [processingSelection, setProcessingSelection] = useState(false);
  const [searching, setSearching] = useState(false);
  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      locationSearch: sessionStorage.getItem("zipCode") || "",
    },
  });

  const handledLocationIdRef = useRef(null);

  const parseGeocodeResult = (result) => {
    const components = result?.address_components || [];
    const getLong = (type) =>
      components.find((x) => x.types.includes(type))?.long_name || null;
    const getShort = (type) =>
      components.find((x) => x.types.includes(type))?.short_name || null;

    return {
      homeLat: result?.geometry?.location?.lat?.() || null,
      homeLong: result?.geometry?.location?.lng?.() || null,
      homeAddr1: result?.formatted_address?.split(",")?.[0] || null,
      city: getLong("locality"),
      county: getLong("administrative_area_level_2"),
      state: getShort("administrative_area_level_1"),
      zip: getLong("postal_code"),
      country: utilities.normalizeCountryForPlus(getLong("country"), "USA"),
      countryCode: utilities.normalizeCountryForPlus(getShort("country"), "USA"),
    };
  };

  const geocodeSearch = async (address) => {
    if (!isLoaded || !window.google?.maps?.Geocoder) {
      throw new Error(t("steps.googleMapsNotReady"));
    }

    const geocoder = new window.google.maps.Geocoder();
    const query =
      address.includes("USA") || address.includes("United States")
        ? address
        : `${address}, USA`;

    const response = await geocoder.geocode({ address: query });
    const first = response?.results?.[0];

    if (!first) {
      throw new Error(t("steps.noMatchingLocationFound"));
    }

    return parseGeocodeResult(first);
  };

  const handleSearchSubmit = async ({ locationSearch }) => {
    const searchTerm = locationSearch?.trim();
    if (!searchTerm) {
      return;
    }

    setSearching(true);
    clearErrors("locationSearch");

    try {
      const googleData = await geocodeSearch(searchTerm);
      sessionStorage.setItem("zipCode", googleData?.zip || searchTerm);
      sessionStorage.setItem("googleMapData", JSON.stringify(googleData));

      handledLocationIdRef.current = null;
      setSelectedLocation(null);
      setSelectedId(null);
      setStepSchedule(STEPS.SCHEDULE_NOW);
      setModalOpen(true);
    } catch (error) {
      setError("locationSearch", {
        type: "manual",
        message: error?.message || t("steps.unableToLoadLocation"),
      });
    } finally {
      setSearching(false);
    }
  };

  const buildAddress = (location) => {
    if (!location) return "";
    const parts = [
      location?.address1,
      location?.address2,
      location?.city,
      location?.state,
      location?.zip,
    ]
      .filter(Boolean)
      .join(", ");
    return parts.includes("USA") ? parts : `${parts}, USA`;
  };

  const geocodeSelection = async (location) => {
    const cached = JSON.parse(sessionStorage.getItem("googleMapData")) || {};

    if (cached?.city && cached?.state && cached?.zip && cached?.county) {
      return {
        city: cached.city,
        county: cached.county,
        state: cached.state,
        zip: cached.zip,
        country: cached.country || null,
        countryCode: cached.countryCode || null,
        homeLat: cached.homeLat || location?.latitude || null,
        homeLong: cached.homeLong || location?.longitude || null,
      };
    }

    if (!isLoaded || !window.google?.maps?.Geocoder) {
      return {
        city: cached?.city || location?.city || null,
        county: cached?.county || null,
        state: cached?.state || location?.state || null,
        zip: cached?.zip || location?.zip || null,
        country: utilities.normalizeCountryForPlus(cached?.country, "USA"),
        countryCode: utilities.normalizeCountryForPlus(
          cached?.countryCode,
          "USA",
        ),
        homeLat: cached?.homeLat || location?.latitude || null,
        homeLong: cached?.homeLong || location?.longitude || null,
      };
    }

    const geocoder = new window.google.maps.Geocoder();
    const response = await geocoder.geocode({
      address: buildAddress(location),
    });
    const first = response?.results?.[0];

    if (!first) {
      return {
        city: cached?.city || location?.city || null,
        county: cached?.county || null,
        state: cached?.state || location?.state || null,
        zip: cached?.zip || location?.zip || null,
        country: utilities.normalizeCountryForPlus(cached?.country, "USA"),
        countryCode: utilities.normalizeCountryForPlus(
          cached?.countryCode,
          "USA",
        ),
        homeLat: cached?.homeLat || location?.latitude || null,
        homeLong: cached?.homeLong || location?.longitude || null,
      };
    }

    const components = first?.address_components || [];
    const getLong = (type) =>
      components.find((x) => x.types.includes(type))?.long_name || null;
    const getShort = (type) =>
      components.find((x) => x.types.includes(type))?.short_name || null;

    const googleLocation = {
      city: getLong("locality") || location?.city || null,
      county: getLong("administrative_area_level_2") || cached?.county || null,
      state: getShort("administrative_area_level_1") || location?.state || null,
      zip: getLong("postal_code") || location?.zip || null,
      country: utilities.normalizeCountryForPlus(
        getLong("country") || cached?.country,
        "USA",
      ),
      countryCode: utilities.normalizeCountryForPlus(
        getShort("country") || cached?.countryCode,
        "USA",
      ),
      homeLat: first?.geometry?.location?.lat?.() || location?.latitude || null,
      homeLong:
        first?.geometry?.location?.lng?.() || location?.longitude || null,
    };

    sessionStorage.setItem(
      "googleMapData",
      JSON.stringify({
        ...cached,
        ...googleLocation,
        homeAddr1:
          first?.formatted_address?.split(",")?.[0] || cached?.homeAddr1,
      }),
    );

    return googleLocation;
  };

  useEffect(() => {
    if (
      stepSchedule !== STEPS.DATE_TIME ||
      !selectedLocation ||
      !selectedLocation?.locationID ||
      handledLocationIdRef.current === selectedLocation.locationID
    ) {
      return;
    }

    handledLocationIdRef.current = selectedLocation.locationID;
    setProcessingSelection(true);

    geocodeSelection(selectedLocation)
      .then((googleLocation) => {
        onLocationSelected({
          location: selectedLocation,
          googleLocation,
        });
      })
      .finally(() => {
        setProcessingSelection(false);
        setModalOpen(false);
      });
  }, [stepSchedule, selectedLocation, onLocationSelected]);

  return (
    <Grid sx={{ width: "100%", maxWidth: "480px", mx: "auto" }}>
      <Grid sx={{ textAlign: "center", mt: 1, mb: 3 }}>
        <Typography
          sx={{ fontWeight: 900, fontSize: "22px", color: "#1c355e" }}
        >
          {t("steps.selectLocationTitle")}
        </Typography>
        <Typography sx={{ mt: 1 }}>
          {t("steps.selectLocationDescription")}
        </Typography>
      </Grid>

      <form onSubmit={handleSubmit(handleSearchSubmit)}>
        <Grid
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
            flexDirection: "column",
            mb: 1,
          }}
        >
          <TextField
            fullWidth
            size="small"
            label={t("steps.zipOrCityState")}
            disabled={searching || submitting}
            error={!!errors.locationSearch}
            helperText={errors.locationSearch?.message || ""}
            {...register("locationSearch", {
              required: t("steps.enterZipOrCityState"),
            })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={searching || submitting}
            sx={{
              minWidth: "110px",
              fontWeight: 700,
              backgroundColor: "#76bc21",
            }}
          >
            {t("steps.submit")}
          </Button>
        </Grid>
      </form>

      <Dialog
        fullWidth
        fullScreen={fullScreen}
        maxWidth="lg"
        open={modalOpen}
        onClose={() => {
          if (!processingSelection) {
            setModalOpen(false);
          }
        }}
        disableEscapeKeyDown={processingSelection}
      >
        <DialogContent sx={{ overflow: "auto" }}>
          {stepSchedule === STEPS.SCHEDULE_NOW ? (
            <ScheduleNow
              selectedLocation={selectedLocation}
              setSelectedLocation={setSelectedLocation}
              setStepSchedule={setStepSchedule}
              STEPS={STEPS}
              isLoaded={isLoaded}
              setGetMap={setGetMap}
              getMap={getMap}
              submitting={submitting}
              selectedId={selectedId}
              setSelectedId={setSelectedId}
            />
          ) : (
            <Grid
              sx={{
                minHeight: 240,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                flexDirection: "column",
              }}
            >
              <RingLoader />
              <Typography>
                {processingSelection
                  ? t("steps.savingYourLocation")
                  : t("steps.loading")}
              </Typography>
            </Grid>
          )}
        </DialogContent>
      </Dialog>
    </Grid>
  );
};

PricingTextPilotLocationStep.propTypes = {
  isLoaded: PropTypes.bool,
  onLocationSelected: PropTypes.func,
  submitting: PropTypes.bool,
};

export default PricingTextPilotLocationStep;
